/**
 * Centralized `/login` redirect URL builder — the single source of truth for
 * constructing the post-authentication return URL.
 *
 * Consolidates logic previously duplicated (and inconsistently encoded) across
 * `src/auth/errorHandler.jsx`, `src/routes/AuthenticatedRoute.jsx`, and
 * `src/components/Navigation/AdminNavigation/index.jsx`.
 */

/**
 * Build the `/login` URL, preserving the current path and query in an encoded
 * `redirect` parameter so the user is returned to where they were after
 * re-authenticating.
 *
 * The path and query are `encodeURIComponent`-encoded so that a `search`
 * containing `&`, `=`, or `#` is carried as a single `redirect` value rather
 * than being parsed as additional top-level query params. The sink in
 * `src/components/Auth/AuthLogin/index.jsx` decodes it via `decodeURIComponent`.
 * @param {Location|{pathname: string, search: string}} [location] - Location
 *   source; defaults to `window.location`. Router call sites should pass
 *   `match.location`.
 * @param {string} [reason] - Optional reason for the login redirect
 * @returns {string} the `/login` URL with an encoded `redirect` parameter
 */
function buildLoginRedirectUrl(location = window.location, reason = null) {
  const { pathname, search } = location;
  const redirectPath = encodeURIComponent(`${pathname}${search}`);
  const reasonParam = reason ? `&reason=${encodeURIComponent(reason)}` : '';
  return `/login?redirect=${redirectPath}${reasonParam}`;
}

export default buildLoginRedirectUrl;
